import type { RequestInfo } from '@ember-data/request';
import { buildBaseURL, type QueryUrlOptions } from '@warp-drive/utilities';
import { SkipCache } from '@warp-drive/core-types/request';

const accountsUrl = (path?: string) => {
  const opts: QueryUrlOptions = {
    identifier: { type: 'accounts' },
    op: 'query',
    resourcePath: 'accounts',
  };
  const base = buildBaseURL(opts);
  return path ? `${base}/${path.replace(/^\/+/, '')}` : base;
};

/**
 * Fetches link-flow state for a (user, financial institution) pair. The
 * legacy AccountAdapter#fetchLinkingInfo previously checked a
 * `spreadsheet-provider` localStorage blob and replayed it with
 * `step = 6` when the stored institution matched; that key is never
 * written anywhere in the codebase, the local read was already labelled
 * `pointlessSpreadsheetCode`, and the cleanup line was commented out —
 * so the replay branch is dropped here. Reintroduce in the caller if a
 * provider that actually writes the blob comes back.
 */
export function fetchLinkingInfoRequest(
  userId: string,
  institutionId: string,
  step = 1
): RequestInfo {
  return {
    url: accountsUrl('link'),
    method: 'PUT',
    body: JSON.stringify({
      step,
      user: { id: userId },
      financialInstitution: { id: institutionId },
    }),
    cacheOptions: { [SkipCache]: true },
  };
}

export function deactivateAccountRequest(accountId: string): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'accounts' },
    op: 'query',
    resourcePath: 'accounts',
  };
  const url = buildBaseURL(urlOptions);
  return {
    url: `${url}/deactivate/${accountId}`,
    method: 'DELETE',
    cacheOptions: {
      [SkipCache]: true,
    },
  };
}

export function updateSnapshotRequest(accountId: string): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'accounts' },
    op: 'query',
    resourcePath: 'accounts',
  };
  const url = buildBaseURL(urlOptions);
  return {
    url: `${url}/${accountId}/update-snapshot`,
    method: 'PATCH',
    cacheOptions: {
      [SkipCache]: true,
    },
  };
}

export function markPaidRequest(accountId: string): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'accounts' },
    op: 'query',
    resourcePath: 'accounts',
  };
  const url = buildBaseURL(urlOptions);
  return {
    url: `${url}/${accountId}/pay-it-off`,
    method: 'POST',
    cacheOptions: {
      [SkipCache]: true,
    },
  };
}

export function createViaProxyRequest(formModel: Record<string, unknown>): RequestInfo {
  const urlOptions: QueryUrlOptions = {
    identifier: { type: 'accounts' },
    op: 'query',
    resourcePath: 'accounts',
  };
  const url = buildBaseURL(urlOptions);
  const body = JSON.stringify(formModel);
  return {
    url: `${url}/proxy`,
    method: 'POST',
    body,
    cacheOptions: {
      [SkipCache]: true,
    },
  };
}
